import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import ShopPromoCarousel from './shopItems/ShopPromoCarousel';
import ShopProductContainer from './shopItems/ShopProductContainer';
import ShopProductTabs from './shopItems/ShopProductTabs';
import ProductProp, { PACK_TYPE } from './propTypes/productProp';
import ShopCartContext from '../../contextProviders/ShopCartContext';
import Notifications from '../../services/notifications';
import { useQuery } from '../../services/customHooks';

const priceToUse = variation => {
  const {
    originalPriceWithVAT: priceTotalVAT,
    originalPriceWithoutVAT: priceTotalNoVAT,
  } = variation?.userPrice || {};
  const {
    originalPriceWithVAT: promotionTotalVAT,
    originalPriceWithoutVAT: promotionTotalNoVAT,
  } = variation?.userPromotionPrice || {};

  // This price will be convert to the correct (company or not) in ShopProductVariationPriceDisplay
  return {
    mainPrice:
      typeof promotionTotalVAT === 'number' && promotionTotalVAT >= 0
        ? promotionTotalVAT
        : priceTotalVAT || 0,
    minorPrice:
      typeof promotionTotalNoVAT === 'number' && promotionTotalNoVAT >= 0
        ? promotionTotalNoVAT
        : priceTotalNoVAT || 0,
  };
};

const getHybridPackPrices = (product, newVariations) => {
  const totalVariations = [
    ...(product?.packVariations || []),
    ...(newVariations?.map(v => ({
      quantity: v.quantity,
      _id: v._id,
      variation: v,
    })) || []),
  ];
  const variationPriceTotal = totalVariations.reduce((acc, v) => {
    const price = priceToUse(v.variation).mainPrice * (v.quantity || 1);
    return acc + price;
  }, 0);
  const variationMinorPriceTotal = totalVariations.reduce((acc, v) => {
    const price = priceToUse(v.variation).minorPrice * (v.quantity || 1);
    return acc + price;
  }, 0);

  const totalVA = totalVariations.reduce((acc, v) => {
    if (v.variation.va && v.variation.commissionable) {
      return acc + (v.quantity || 1) * v.variation.va;
    }
    return acc;
  }, 0);
  const totalCV = totalVariations.reduce((acc, v) => {
    if (v.variation.cv && v.variation.commissionable) {
      return acc + (v.quantity || 1) * v.variation.cv;
    }
    return acc;
  }, 0);

  return { variationPriceTotal, variationMinorPriceTotal, totalVA, totalCV };
};

function ProductPage({
  t,
  product,
  directSellingActive,
  isMarketPlaceEnabled,
}) {
  const query = useQuery();
  const journeySlug = query.get('journey');

  const { shopCart, setShopCart } = useContext(ShopCartContext);
  const { isPack, pack = {} } = product;
  const { amountVariationsPerAdvancedPack, packType } = pack || {};
  const isAdvancedPack = packType === PACK_TYPE.ADVANCED_PACK;
  const isHybridPack = packType === PACK_TYPE.HYBRID_PACK;
  const [packVariations, setPackVariations] = useState([]);

  // blur page if no product
  useEffect(() => {
    if (!product?._id) {
      document.querySelectorAll('.m-grid__item').forEach(element => {
        // eslint-disable-next-line no-param-reassign
        element.style.filter = 'blur(8px)';
      });
    }
  }, []);
  const [productItems, setProductItems] = useState(
    product.variations.map(v => ({
      id: v._id,
      price: priceToUse(v).mainPrice / 100, // Select the correct price to use
      minorPrice: priceToUse(v).minorPrice / 100, // Select the correct price to use
      amount: 0,
      va: v.va,
      cv: v.cv,
      commissionable: v.commissionable,
      image: v.icon.thumb,
    })),
  );
  const [packQuantity, setPackQuantity] = useState(1);

  const packPrices = userPrice => {
    const isIndividual =
      userPrice.billingType === 'individual' || !userPrice.billingType;
    return {
      priceTotal:
        (!isIndividual
          ? userPrice.originalPriceWithoutVAT
          : userPrice.originalPriceWithVAT) / 100,
      minorPriceTotal:
        (!isIndividual
          ? userPrice.originalPriceWithVAT
          : userPrice.originalPriceWithoutVAT) / 100,
    };
  };
  const [priceTotal, setPriceTotal] = useState(
    isPack
      ? packPrices(pack?.userPromotionPrice || pack.userPrice)?.priceTotal
      : 0,
  );
  const [minorPriceTotal, setMinorPriceTotal] = useState(
    isPack
      ? packPrices(pack?.userPromotionPrice || pack.userPrice)?.minorPriceTotal
      : 0,
  );

  const [totalVA, setTotalVA] = useState(0);
  const [totalCV, setTotalCV] = useState(0);

  const handleChangeVariations = useCallback(
    newVariations => {
      setPackVariations(newVariations);

      if (pack.packType === PACK_TYPE.HYBRID_PACK) {
        const {
          variationMinorPriceTotal,
          variationPriceTotal,
          totalVA: hybridTotalVA,
          totalCV: hybridTotalCV,
        } = getHybridPackPrices(product, newVariations);
        setPriceTotal(variationPriceTotal / 100);
        setMinorPriceTotal(variationMinorPriceTotal / 100);
        setTotalVA(hybridTotalVA);
        setTotalCV(hybridTotalCV);
      }
    },
    [
      setPackVariations,
      pack,
      product,
      setPriceTotal,
      setMinorPriceTotal,
      setTotalVA,
      setTotalCV,
    ],
  );

  // state that allow us to compare the quantity order and stock
  const [quantityOfProduct, setQuantityOfProduct] = useState([]);
  const [addToCartLocked, setAddToCarLocked] = useState(true);

  useEffect(() => {
    if (!isPack) {
      if (productItems?.length > 0) {
        const shouldUnlock = productItems.filter(p => p.amount > 0);
        setAddToCarLocked(!(shouldUnlock.length > 0));
      }
    } else if (isAdvancedPack) {
      const packVariationQuantities =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;
      setAddToCarLocked(
        // eslint-disable-next-line no-use-before-define
        packVariationQuantities !== amountVariationsPerAdvancedPack,
      );
    } else if (
      isHybridPack &&
      product?.multiPackVariationsAvailable?.length > 0
    ) {
      const packVariationQuantities =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;
      const amountVariationsToChoose =
        product?.multiPackVariationsAvailable?.reduce(
          (acc, item) => acc + parseInt(item.amountVariationsToChoose, 10),
          0,
        ) || 0;
      setAddToCarLocked(
        // eslint-disable-next-line no-use-before-define
        packVariationQuantities !== amountVariationsToChoose,
      );
    } else {
      setAddToCarLocked(packQuantity === 0);
    }
  }, [quantityOfProduct, packQuantity, packVariations]);
  const changeProductQuantity = event => {
    const { id } = event.target;
    const value = parseInt(event.target.value, 10);
    const key = id.slice(id.length - 24);
    const productSelected = product.variations.find(prod => prod._id === key);
    setQuantityOfProduct(prevState => {
      const productAlreadyExisting = prevState.find(
        p => p._id === productSelected._id,
      );
      if (productAlreadyExisting) {
        productAlreadyExisting.quantity = value;
        return [...prevState];
      }
      return [
        ...prevState,
        {
          _id: productSelected._id,
          stock: productSelected.stockQuantity,
          quantity: value,
        },
      ];
    });
    setProductItems(prevItems => {
      const newProductItems = prevItems.map(item => {
        if (item.id === key) {
          return {
            ...item,
            amount: value >= 0 ? value : 0,
          };
        }
        return item;
      });
      setPriceTotal(
        (
          Math.round(
            newProductItems.reduce((r, i) => r + i.amount * i.price, 0) * 100,
          ) / 100
        ).toFixed(2),
      );
      setMinorPriceTotal(
        (
          Math.round(
            newProductItems.reduce((r, i) => r + i.amount * i.minorPrice, 0) *
              100,
          ) / 100
        ).toFixed(2),
      );
      setTotalVA(
        newProductItems.reduce((r, i) => {
          if (i.va && i.commissionable) {
            return r + i.amount * i.va;
          }
          return r;
        }, 0),
      );
      setTotalCV(
        newProductItems.reduce((r, i) => {
          if (i.cv && i.commissionable) {
            return r + i.amount * i.cv;
          }
          return r;
        }, 0),
      );
      return newProductItems;
    });
  };

  const checkIfAdvancedPackIsValid = () => {
    if (isHybridPack && product?.multiPackVariationsAvailable?.length > 0) {
      const numberOfProducts =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;
      const amountVariationsToChoose =
        product?.multiPackVariationsAvailable?.reduce(
          (acc, item) => acc + parseInt(item.amountVariationsToChoose, 10),
          0,
        ) || 0;

      if (numberOfProducts !== amountVariationsToChoose) {
        Notifications.showNotificationWarning(
          t('warning'),
          t(
            'advancedPack.error.amountVariationsPerAdvancedPack',
            amountVariationsToChoose,
          ),
        );
        return false;
      }
    }
    if (isAdvancedPack) {
      const numberOfProducts =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;

      if (numberOfProducts !== amountVariationsPerAdvancedPack) {
        Notifications.showNotificationWarning(
          t('warning'),
          t(
            'advancedPack.error.amountVariationsPerAdvancedPack',
            amountVariationsPerAdvancedPack,
          ),
        );
        return false;
      }
    }
    return true;
  };

  /**
   * Updates the prices, commissionable's and quantity state
   * @param event {string} Can be 'minus' or 'plus
   * @type {(function(*): void)|*}
   */
  const changePackQuantity = useCallback(
    event => {
      const isValid = checkIfAdvancedPackIsValid();
      if (!isValid) return;

      setPackQuantity(prevState => {
        const newQuantity = event === 'plus' ? prevState + 1 : prevState - 1;
        if (packType === PACK_TYPE.HYBRID_PACK) {
          const {
            variationMinorPriceTotal,
            variationPriceTotal,
            totalVA: hybridTotalVA,
            totalCV: hybridTotalCV,
          } = getHybridPackPrices(product, packVariations);

          setPriceTotal((variationPriceTotal / 100) * newQuantity);
          setMinorPriceTotal((variationMinorPriceTotal / 100) * newQuantity);
          setTotalVA(hybridTotalVA * newQuantity);
          setTotalCV(hybridTotalCV * newQuantity);
          return newQuantity;
        }
        // OTHER PACKS
        setPriceTotal(
          (priceToUse(product?.pack).mainPrice / 100) * newQuantity,
        );
        setMinorPriceTotal(
          (priceToUse(product?.pack).minorPrice / 100) * newQuantity,
        );
        if (product?.pack?.commissionable) {
          setTotalVA((product?.pack?.va || 0) * newQuantity);
          setTotalCV((product?.pack?.cv || 0) * newQuantity);
        }
        return newQuantity;
      });
    },
    [setPackQuantity, packVariations],
  );

  const addPackToCart = (isBuyNow = false, subscriptionType = null) => {
    // Validate subscription frequency compatibility with existing cart items
    if (subscriptionType && !isBuyNow) {
      const conflictingProduct = shopCart.find(
        p =>
          p.subscriptionType &&
          p.subscriptionType !== subscriptionType,
      );
      if (conflictingProduct) {
        Notifications.showNotificationError(
          t('error'),
          t(
            'shop.product.warning.message.error.subscriptionFrequencyConflict',
          ),
        );
        return;
      }
    }

    let cartPackVariations = isAdvancedPack
      ? packVariations.map(v => ({ ...v, variation: v }))
      : product?.packVariations;

    if (isHybridPack) {
      const productVariations =
        product?.packVariations?.map(p => ({
          ...p,
          _id: p?.variation?._id,
        })) || [];

      const selectedVariations =
        packVariations?.map(v => ({ ...v, variation: v })) || [];

      // Merge and sum quantities for matching variations
      const variationMap = new Map();

      [...productVariations, ...selectedVariations].forEach(item => {
        const existingItem = variationMap.get(item._id);
        if (existingItem) {
          existingItem.quantity =
            (existingItem.quantity || 0) + (item.quantity || 0);
        } else {
          variationMap.set(item._id, { ...item });
        }
      });

      cartPackVariations = Array.from(variationMap.values());
    }

    // region Create Product to add and increase amount or simply add it
    const packToAdd = {
      id: product._id,
      price: priceTotal,
      minorPrice: minorPriceTotal,
      amount: packQuantity,
      va: totalVA || 0,
      cv: totalCV,
      commissionable: product?.pack?.commissionable,
      image: product?.images[0]?.thumb,
      isPack: true,
      packVariations: cartPackVariations,
      subscriptionType,
      availableForSubscription: product.availableForSubscription,
      availableSubscriptionFrequency: product.availableSubscriptionFrequency,
      isAdvancedPack,
      isHybridPack,
      amountVariationsPerAdvancedPack,
    };
    // endregion Create Product to add and increase amount or simply add it

    // region Ensure that the pack does not exceed the available stock.
    let updatedCart;
    if (!isBuyNow) {
      const isAlreadyInCart = shopCart.find(p => p.id === product._id);
      // if Product already exists, adds up the quantities
      // else, just add it in cart
      updatedCart = isAlreadyInCart
        ? shopCart.map(p => {
            if (p.id === product._id) {
              return {
                ...p,
                amount: p.amount + packQuantity,
                packVariations: isHybridPack
                  ? cartPackVariations
                  : product?.packVariations,
              };
            }
            return { ...p, amount: packQuantity };
          })
        : [...shopCart, packToAdd];
    } else {
      updatedCart = [packToAdd];
    }
    // stockValid will be true if all variations have sufficient stock, otherwise false
    const productToCheck = updatedCart?.find(p => p.id === product._id);
    const stockValid = productToCheck?.packVariations?.every(v => {
      const { variation, quantity } = v;
      const { stockQuantity } = variation;
      const fullQuantity = quantity * (productToCheck?.amount || 0); // Quantity of variation * Amount of packs
      if (typeof stockQuantity === 'number') {
        // If there is stock selected for variation
        return stockQuantity >= fullQuantity;
      }
      return true; // stock is null
    });

    /**
     * TODO: Get cart in real time, to check also the quantities that were previously added
     */
    if (!stockValid) {
      Notifications.showErrorFromRequest(
        null,
        'shop.product.warning.message.error.quantityAboveStock',
      );
      return;
    }
    // endregion Ensure that the pack does not exceed the available stock.

    const cartProducts = isBuyNow ? [packToAdd] : updatedCart;

    setShopCart(
      cartProducts.map(cartProduct => {
        const newCartProduct = { ...cartProduct };

        // Only delete packVariations if it's not an advanced pack
        if (
          !cartProduct?.isAdvancedPack &&
          !cartProduct?.isHybridPack &&
          newCartProduct.packVariations
        ) {
          delete newCartProduct.packVariations;
        } else if (cartProduct?.isAdvancedPack || cartProduct?.isHybridPack) {
          newCartProduct.packVariations = newCartProduct.packVariations.map(
            variation => ({
              _id: variation._id,
              quantity: variation.quantity,
            }),
          );
        }
        return newCartProduct;
      }),
    );

    if (isBuyNow) {
      window.location = `/shop/checkout${
        journeySlug ? `?journey=${journeySlug}` : ''
      }`;
    }
  };
  const addToCart = (event, subscriptionType = null) => {
    event.preventDefault();

    // Validate subscription frequency compatibility with existing cart items
    if (subscriptionType) {
      const conflictingProduct = shopCart.find(
        p =>
          p.subscriptionType &&
          p.subscriptionType !== subscriptionType,
      );
      if (conflictingProduct) {
        Notifications.showNotificationError(
          t('error'),
          t(
            'shop.product.warning.message.error.subscriptionFrequencyConflict',
          ),
        );
        return;
      }
    }

    if (!isPack) {
      // region normal variations
      // in case of quantity greater than stock show notification error, if stock is null ignore
      const checkStockIsNotNull = quantityOfProduct.find(p => {
        if (p.stock === null) {
          return false;
        }
        return p.stock - p.quantity < 0;
      });
      // if array state has products and there are products were the stock is not null
      if (quantityOfProduct.length > 0 && checkStockIsNotNull) {
        Notifications.showErrorFromRequest(
          null,
          'shop.product.warning.message.error.quantityAboveStock',
        );
        return;
      }

      const nonEmptyProducts = productItems.filter(v => v.amount > 0);
      const emptyProducts = productItems.filter(v => v.amount === 0);
      const updatedProduct = [
        ...shopCart
          .filter(p => nonEmptyProducts.find(pi => pi.id === p.id))
          .map(p => ({
            ...nonEmptyProducts.find(pi => pi.id === p.id),
            amount:
              nonEmptyProducts.find(pi => pi.id === p.id).amount + p.amount,
          })),
        ...shopCart.filter(p => emptyProducts.find(pi => pi.id === p.id)),
        ...shopCart.filter(
          p =>
            !nonEmptyProducts.find(pi => pi.id === p.id) &&
            !emptyProducts.find(pi => pi.id === p.id),
        ),
      ];
      const cartItems = [
        ...updatedProduct,
        ...nonEmptyProducts.filter(
          p => !updatedProduct.find(up => up.id === p.id) && p.amount,
        ),
      ];
      setShopCart(
        subscriptionType
          ? cartItems.map(p => {
              const isCurrentProduct = nonEmptyProducts.some(
                np => np.id === p.id,
              );
              if (isCurrentProduct) {
                return {
                  ...p,
                  subscriptionType,
                  availableForSubscription:
                    product.availableForSubscription,
                  availableSubscriptionFrequency:
                    product.availableSubscriptionFrequency,
                };
              }
              return p;
            })
          : cartItems,
      );
      // endregion normal variations
    } else {
      addPackToCart(false, subscriptionType);
    }
  };

  const handleBuyNow = (event, subscriptionType = null) => {
    event.preventDefault();
    if (!isPack) {
      // region normal variations
      // in case of quantity greater than stock show notification error, if stock is null ignore
      const checkStockIsNotNull = quantityOfProduct.find(p => {
        if (p.stock === null) {
          return false;
        }
        return p.stock - p.quantity < 0;
      });
      // if array state has products and there are products were the stock is not null
      if (quantityOfProduct.length > 0 && checkStockIsNotNull) {
        Notifications.showErrorFromRequest(
          null,
          'shop.product.warning.message.error.quantityAboveStock',
        );
        return;
      }

      let nonEmptyProducts = productItems.filter(v => v.amount > 0);

      // External-payment-link products are charged by a fixed-price Stripe
      // Payment Link on an external account: exactly one variation, never
      // mixed with other cart items (BRAVON-6316; the backend enforces the
      // same rule). Quantity is locked to 1 unless the product allows it —
      // the paid quantity is then reconciled from Stripe after checkout.
      if (product?.externalPaymentLink) {
        nonEmptyProducts = nonEmptyProducts.slice(0, 1).map(p => ({
          ...p,
          amount: product.externalPaymentAllowQuantity ? p.amount : 1,
          externalPaymentLink: product.externalPaymentLink,
        }));
      }

      const finalCartCtx = subscriptionType
        ? nonEmptyProducts.map(p => ({
            ...p,
            subscriptionType,
            availableForSubscription: product.availableForSubscription,
            availableSubscriptionFrequency:
              product.availableSubscriptionFrequency,
          }))
        : nonEmptyProducts;
      setShopCart(finalCartCtx);
      window.location = `/shop/checkout${
        journeySlug ? `?journey=${journeySlug}` : ''
      }`;
      // endregion normal variations
    } else {
      addPackToCart(true, subscriptionType);
    }
  };

  return (
    <Title title={t('shop.productDetail')}>
      <ContentContainer>
        <SubHeader title={t('shop.productDetail')} />
        <div className="m-content shop mt-5">
          <div className="row mt-5">
            <div className="col-lg-12">
              <ShopProductContainer
                product={product}
                changeProductQuantity={changeProductQuantity}
                onPackQuantityChange={changePackQuantity}
                packQuantity={packQuantity}
                priceTotal={priceTotal}
                minorPriceTotal={minorPriceTotal}
                totalVA={totalVA}
                totalCV={totalCV}
                productItems={productItems}
                addToCart={addToCart}
                onBuyNow={handleBuyNow}
                directSellingActive={directSellingActive}
                addToCartLocked={addToCartLocked}
                isMarketPlaceEnabled={isMarketPlaceEnabled}
                packVariations={packVariations}
                handleChangeVariations={handleChangeVariations}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 product-details-tabs mt-4">
              <span className="product-details-title">
                {t('shop.productDetail')}
              </span>
              <ShopProductTabs product={product} />
            </div>

            {product?._id ? (
              <div id="productDetailCarousel" className="col-lg-12 col-md-12">
                <ShopPromoCarousel
                  title={t('shop.product.related')}
                  t={t}
                  type="similar"
                  productInfo={product._id}
                  productList={[]}
                />
              </div>
            ) : null}
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

ProductPage.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType),
  directSellingActive: PropTypes.bool.isRequired,
  isMarketPlaceEnabled: PropTypes.bool,
};

ProductPage.defaultProps = {
  isMarketPlaceEnabled: false,
  product: ProductProp.defaultProp,
};

export default translate(ProductPage);
